import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import {
  Box,
  Button,
  IconButton,
  Modal,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { fetchCollateral, saveCollateral } from '@react/fetchers/collateralFetchers'
import { useOtChainState } from '@react/redux/store'
import { CollateralSetCTO, DefiCollateralCTO, UnderlyingCollateralCTO } from '@shared/CollateralSetCTO'
import React, { useEffect, useState } from 'react'
import DefiCollateralForm from './DefiCollateralForm'
import DefiCollateralView from './DefiCollateralView'
import UnderlyingCollateralForm from './UnderlyingCollateralForm'

type Props = {
  poolAddr: string
  readOnly?: boolean
}

export default function CollateralView({ poolAddr, readOnly = false }: Props): React.ReactElement {
  const {
    stageConfiguration: { restAPIKey, serverApiBaseUrl },
    cognitoUser,
  } = useOtChainState()
  const [isDefi, setIsDefi] = useState(false)

  const [rows, setRows] = useState<Array<UnderlyingCollateralCTO | DefiCollateralCTO>>([])

  const [editIndex, setEditIndex] = useState<number | null>(null)
  const [isAdding, setIsAdding] = useState(false)

  // Fetch collateral data from backend
  const fetchAndSetCollateral = () => {
    if (poolAddr && serverApiBaseUrl && restAPIKey) {
      fetchCollateral(serverApiBaseUrl, restAPIKey, poolAddr).then((_collateralSetCTO) => {
        if (!_collateralSetCTO) {
          setIsDefi(false)
          setRows([])
        } else {
          setIsDefi(!!_collateralSetCTO.isDefi)
          setRows(_collateralSetCTO.collateral ?? [])
        }
      })
    }
  }

  useEffect(() => {
    fetchAndSetCollateral()
  }, [poolAddr, serverApiBaseUrl, restAPIKey])

  const handleEdit = (idx: number) => setEditIndex(idx)
  const handleDelete = (idx: number) => {
    if (readOnly) {
      return
    }
    const newRows = rows.filter((_, i) => i !== idx)
    saveCollateralArray(newRows)
  }

  const handleAddTraditional = () => {
    setIsAdding(true)
  }
  const handleAddDefi = () => {
    setIsAdding(true)
  }

  const handleSave = (item: UnderlyingCollateralCTO | DefiCollateralCTO, idx: number | null) => {
    if (readOnly) {
      return
    }
    let newRows: Array<UnderlyingCollateralCTO | DefiCollateralCTO>
    if (idx === null) {
      // Add
      newRows = [...rows, item]
    } else {
      // Edit
      newRows = rows.map((row, i) => (i === idx ? item : row))
    }

    saveCollateralArray(newRows)

    setIsAdding(false)
    setEditIndex(null)
  }

  const saveCollateralArray = async (collateralArray: Array<UnderlyingCollateralCTO | DefiCollateralCTO>) => {
    try {
      setRows(collateralArray) // Update local state
    } catch (err) {
      console.error('Failed to save collateral array:', err)
      // Optionally show an error to the user
    }
  }
  const saveCollateralSetCTO = async () => {
    const newCollateralSetCTO = {
      isDefi,
      poolAddr,
      collateral: isDefi ? (rows as DefiCollateralCTO[]) : (rows as UnderlyingCollateralCTO[]),
      timestamp: 0,
    } as CollateralSetCTO
    await saveCollateral(serverApiBaseUrl, newCollateralSetCTO, cognitoUser)
  }

  const modalBoxSx = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 800,
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: 2,
    p: 4,
  }

  if (isDefi) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexDirection: 'column' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            justifyContent: 'flex-start',
            gap: 1,
            flexDirection: 'row',
          }}
        >
          <Typography variant="bodyMuted" gutterBottom component="div">
            Defi Collateral
          </Typography>
          <Switch
            checked={isDefi}
            onChange={() => {
              setRows([])
              setIsDefi(!isDefi)
              if (!isDefi) {
                handleAddDefi()
              }
            }}
          />
          <Button
            onClick={() => {
              setEditIndex(0)
              setIsAdding(true)
            }}
          >
            Edit
          </Button>

          <Box sx={{ flexGrow: 6 }}></Box>
        </Box>
        <DefiCollateralView poolAddr={poolAddr} />
        <Modal open={isAdding || editIndex !== null} onClose={() => setIsAdding(false)}>
          <Box sx={modalBoxSx}>
            <DefiCollateralForm
              onClose={() => {
                setEditIndex(null)
                setIsAdding(false)
              }}
              poolAddr={poolAddr}
            />
          </Box>
        </Modal>
      </Box>
    )
  }
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexDirection: 'row' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexDirection: 'row' }}>
          <Typography variant="bodyMuted" gutterBottom component="div">
            Traditional Collateral
          </Typography>

          <Switch
            checked={isDefi}
            onChange={() => {
              setRows([])
              setIsDefi(!isDefi)
              if (!isDefi) {
                handleAddDefi()
              }
            }}
          />
          {!readOnly && !isDefi && (
            <Button startIcon={<AddIcon />} onClick={handleAddTraditional}>
              Add Traditional Collateral
            </Button>
          )}
        </Box>

        <Box sx={{ flexGrow: 1 }}></Box>
        {!isDefi && (
          <Box sx={{}}>
            <Button
              onClick={() => {
                fetchAndSetCollateral()
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                saveCollateralSetCTO()
              }}
            >
              Save Changes
            </Button>
          </Box>
        )}
      </Box>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Asset Type</TableCell>
            <TableCell>Asset ID</TableCell>
            <TableCell>Yield/Coupon</TableCell>
            <TableCell>Maturity Date</TableCell>
            <TableCell>Market Value</TableCell>
            {!readOnly && <TableCell>Actions</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {(rows as UnderlyingCollateralCTO[]).map((row: UnderlyingCollateralCTO, idx) => (
            <TableRow key={idx}>
              <TableCell>{row.assetType}</TableCell>
              <TableCell>{row.assetId}</TableCell>
              <TableCell>{typeof row.yieldOrCoupon === 'number' ? `${row.yieldOrCoupon}%` : 'N/A'}</TableCell>
              <TableCell>{row.maturityDate && row.maturityDate.trim() !== '' ? row.maturityDate : 'N/A'}</TableCell>
              <TableCell>{Number(row.marketValue).toLocaleString()}</TableCell>
              {!readOnly && (
                <TableCell>
                  <IconButton onClick={() => handleEdit(idx)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(idx)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Edit Modal */}
      <Modal open={editIndex !== null} onClose={() => setEditIndex(null)}>
        <Box sx={modalBoxSx}>
          {editIndex !== null && (
            <UnderlyingCollateralForm
              index={editIndex}
              collateral={rows[editIndex] as UnderlyingCollateralCTO}
              onSave={handleSave}
              onClose={() => setEditIndex(null)}
              poolAddr={poolAddr}
            />
          )}
        </Box>
      </Modal>
      {/* Add Modal */}
      <Modal open={isAdding} onClose={() => setIsAdding(false)}>
        <Box sx={modalBoxSx}>
          <UnderlyingCollateralForm
            index={null}
            collateral={{ assetType: '', assetId: '', maturityDate: '', marketValue: 0, yieldOrCoupon: 0 }}
            onSave={handleSave}
            onClose={() => {
              setEditIndex(null)
              setIsAdding(false)
            }}
            poolAddr={poolAddr}
          />
        </Box>
      </Modal>
    </>
  )
}
